import {ArrowUpRightIcon} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";

import {useChatContext} from "@contexts/ChatContext";

import {SidePanelChat} from "./SidePanelChat";

const MIN_WIDTH = 300;
const MAX_WIDTH = 520;
const DEFAULT_WIDTH = 340;
const STORAGE_KEY = "pecunio-sidepanel-width";

export function SidePanel() {
    const {sidePanelOpen, setSidePanelOpen, unreadCount, chatFullscreen} = useChatContext();

    const [width, setWidth] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parseInt(stored, 10))) : DEFAULT_WIDTH;
    });

    const isDragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(width);

    // Persist width
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, String(width));
    }, [width]);

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        isDragging.current = true;
        startX.current = e.clientX;
        startWidth.current = width;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        e.preventDefault();
    }, [width]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            const delta = e.clientX - startX.current;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
            setWidth(newWidth);
        };
        const onMouseUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            }
        };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, []);

    if (sidePanelOpen && chatFullscreen) {
        return (
            <div className="fixed inset-0 z-50 flex bg-white">
                <SidePanelChat />
            </div>
        );
    }

    if (!sidePanelOpen) {
        // Collapsed: narrow strip with logo + open arrow
        return (
            <div
                data-tour="agent-panel-rail"
                className="flex flex-col items-center w-[52px] flex-shrink-0 bg-[#0F172A] border-r border-white/[0.06] cursor-pointer hover:bg-[#1E293B] transition-colors"
                onClick={() => setSidePanelOpen(true)}
                title="Open chat"
            >
                <div className="flex flex-col items-center gap-2 py-3.5">
                    <div className="relative w-[30px] h-[30px] rounded-[7px] bg-gradient-to-br from-[#209DB3] to-[#1A8C9E] flex items-center justify-center text-white font-extrabold text-[13px]">
                        P
                        {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#5BF6C8] border-2 border-[#0F172A]" />
                        )}
                    </div>
                    <ArrowUpRightIcon className="size-3.5 text-[#94A3B8]" />
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Expanded panel */}
            <div
                data-tour="agent-panel"
                className="flex flex-col overflow-hidden bg-[#1E293B] border-r border-white/[0.06] transition-[width] duration-200 ease-out"
                style={{width, minWidth: MIN_WIDTH}}
            >
                <SidePanelChat />
            </div>

            {/* Drag handle */}
            <div
                onMouseDown={onMouseDown}
                className="w-[5px] flex-shrink-0 cursor-col-resize relative z-20 group"
            >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[3px] h-10 bg-[#E2E8F0] rounded-full transition-all group-hover:bg-[#209DB3] group-hover:h-[60px] group-hover:w-1" />
            </div>
        </>
    );
}
